import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, ClipboardIcon, CalendarIcon, PhoneIcon, HeartPulseIcon, PillIcon, UserIcon, HomeIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
export const ResidentProfilePage: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  // Sample resident data - in a real app, this would be fetched from an API
  const residents = [{
    id: 1,
    name: 'Eleanor Johnson',
    age: 83,
    photo: 'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    roomNumber: '203',
    admissionDate: '2021-08-15',
    careLevel: 'Assisted Living',
    primaryPhysician: 'Dr. Sarah Williams',
    emergencyContact: 'Michael Johnson (Son)',
    emergencyPhone: '(555) 123-4567',
    status: 'Active',
    dob: '1940-05-12',
    allergies: ['Penicillin', 'Shellfish'],
    diagnoses: ['Hypertension', 'Arthritis', 'Early-stage dementia'],
    medications: [{
      name: 'Lisinopril',
      dosage: '10mg',
      frequency: 'Once daily',
      purpose: 'Blood pressure'
    }, {
      name: 'Furosemide',
      dosage: '20mg',
      frequency: 'Twice daily',
      purpose: 'Diuretic'
    }, {
      name: 'Sertraline',
      dosage: '50mg',
      frequency: 'Once daily',
      purpose: 'Depression'
    }],
    vitalSigns: {
      bloodPressure: '130/82',
      heartRate: '72',
      temperature: '98.6',
      oxygenLevel: '96%',
      lastChecked: '2023-09-15'
    },
    activities: ['Bingo', 'Morning walks', 'Art therapy'],
    dietaryRestrictions: 'Low sodium, soft foods',
    notes: 'Eleanor enjoys reading and participating in group activities. She sometimes gets confused in the evenings.',
    fallRisk: 'High',
    mobility: 'Walker-dependent'
  }, {
    id: 2,
    name: 'Robert Thompson',
    age: 78,
    photo: 'https://images.unsplash.com/photo-1556474835-b0f3ac40d4d1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    roomNumber: '115',
    admissionDate: '2022-02-10',
    careLevel: 'Memory Care',
    primaryPhysician: 'Dr. James Chen',
    emergencyContact: 'Patricia Thompson (Daughter)',
    emergencyPhone: '(555) 987-6543',
    status: 'Active',
    dob: '1945-11-23',
    allergies: ['Sulfa drugs'],
    diagnoses: ["Alzheimer's disease", 'Diabetes Type 2', 'Glaucoma'],
    medications: [{
      name: 'Amlodipine',
      dosage: '5mg',
      frequency: 'Once daily',
      purpose: 'Blood pressure'
    }, {
      name: 'Metoprolol',
      dosage: '25mg',
      frequency: 'Twice daily',
      purpose: 'Heart rate'
    }, {
      name: 'Simvastatin',
      dosage: '20mg',
      frequency: 'At bedtime',
      purpose: 'Cholesterol'
    }],
    vitalSigns: {
      bloodPressure: '128/78',
      heartRate: '68',
      temperature: '98.2',
      oxygenLevel: '95%',
      lastChecked: '2023-09-14'
    },
    activities: ['Memory games', 'Music therapy', 'Supervised walks'],
    dietaryRestrictions: 'Diabetic diet, no added sugar',
    notes: 'Robert responds well to routine. Becomes agitated when schedule changes. Enjoys classical music.',
    fallRisk: 'High',
    mobility: 'Cane-assisted'
  }
  // Additional residents would be here...
  ];
  const resident = residents.find(r => r.id === Number(id));
  if (!resident) {
    return <main className="flex-1 overflow-y-auto bg-gray-50 p-6" data-id="element-365">
        <div className="text-center py-10" data-id="element-366">
          <h2 className="text-xl font-medium text-gray-900" data-id="element-367">
            Resident not found
          </h2>
          <p className="mt-2 text-gray-500" data-id="element-368">
            The resident you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/residents" className="mt-5 inline-flex items-center px-4 py-2 bg-[#1f58b0] text-white rounded-md hover:bg-[#255db7] transition-colors" data-id="element-369">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-370" />
            Back to Residents
          </Link>
        </div>
      </main>;
  }
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  return <main className="flex-1 overflow-y-auto bg-gray-50 p-6" data-id="element-371">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4" data-id="element-372">
        <div className="flex items-center" data-id="element-373">
          <Link to="/residents" className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors" data-id="element-374">
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" data-id="element-375" />
          </Link>
          <h1 className="text-2xl font-bold" data-id="element-376">Resident Profile</h1>
        </div>
        <div className="flex gap-3" data-id="element-377">
          <button className="flex items-center px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors" data-id="element-378">
            <ClipboardIcon className="h-4 w-4 mr-2" data-id="element-379" />
            Care Plan
          </button>
          
        </div>
      </div>
      {/* Resident Header */}
      <div className="bg-white rounded-lg shadow mb-6" data-id="element-380">
        <div className="p-6" data-id="element-381">
          <div className="flex flex-wrap items-start gap-6" data-id="element-382">
            <div className="flex-shrink-0" data-id="element-383">
              <img src={resident.photo} alt={resident.name} className="h-24 w-24 rounded-full object-cover border-4 border-white shadow" data-id="element-384" />
            </div>
            <div className="flex-1" data-id="element-385">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-386">
                {resident.name}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500" data-id="element-387">
                <div className="flex items-center" data-id="element-388">
                  <UserIcon className="h-4 w-4 mr-1" data-id="element-389" />
                  {resident.age} years (
                  {resident.dob ? formatDate(resident.dob) : 'Unknown DOB'})
                </div>
                <div className="flex items-center" data-id="element-390">
                  <HomeIcon className="h-4 w-4 mr-1" data-id="element-391" />
                  Room {resident.roomNumber}
                </div>
                <div className="flex items-center" data-id="element-392">
                  <ClockIcon className="h-4 w-4 mr-1" data-id="element-393" />
                  Admitted {formatDate(resident.admissionDate)}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-394">
                <span className={`px-3 py-1 inline-flex text-sm font-medium rounded-full ${resident.careLevel === 'Memory Care' ? 'bg-purple-100 text-purple-800' : resident.careLevel === 'Assisted Living' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-395">
                  {resident.careLevel}
                </span>
                {resident.fallRisk === 'High' && <span className="px-3 py-1 inline-flex items-center text-sm font-medium rounded-full bg-red-100 text-red-800" data-id="element-396">
                    <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-397" />
                    High Fall Risk
                  </span>}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-398">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-399">
                Emergency Contact
              </h3>
              <p className="text-sm font-medium" data-id="element-400">{resident.emergencyContact}</p>
              {resident.emergencyPhone && <p className="text-sm text-gray-500 flex items-center mt-1" data-id="element-401">
                  <PhoneIcon className="h-3 w-3 mr-1" data-id="element-402" />
                  {resident.emergencyPhone}
                </p>}
            </div>
          </div>
        </div>
      </div>
      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-403">
        {/* Left Column */}
        <div className="lg:col-span-2" data-id="element-404">
          {/* Medical Information */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-405">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-406">
              <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-407">
                <HeartPulseIcon className="h-5 w-5 mr-2 text-[#f97c6b]" data-id="element-408" />
                Medical Information
              </h3>
            </div>
            <div className="p-6" data-id="element-409">
              <div className="mb-6" data-id="element-410">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-411">
                  Diagnoses
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-412">
                  {resident.diagnoses?.map((diagnosis, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full" data-id="element-413">
                      {diagnosis}
                    </span>)}
                </div>
              </div>
              <div className="mb-6" data-id="element-414">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-415">
                  Allergies
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-416">
                  {resident.allergies?.map((allergy, index) => <span key={index} className="px-3 py-1 bg-red-50 text-red-700 text-sm rounded-full" data-id="element-417">
                      {allergy}
                    </span>)}
                </div>
              </div>
              <div className="mb-6" data-id="element-418">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-419">
                  Vital Signs
                </h4>
                {resident.vitalSigns && <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-420">
                    <div className="bg-gray-50 p-3 rounded" data-id="element-421">
                      <div className="text-xs text-gray-500" data-id="element-422">
                        Blood Pressure
                      </div>
                      <div className="text-lg font-medium" data-id="element-423">
                        {resident.vitalSigns.bloodPressure}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded" data-id="element-424">
                      <div className="text-xs text-gray-500" data-id="element-425">Heart Rate</div>
                      <div className="text-lg font-medium" data-id="element-426">
                        {resident.vitalSigns.heartRate} bpm
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded" data-id="element-427">
                      <div className="text-xs text-gray-500" data-id="element-428">Temperature</div>
                      <div className="text-lg font-medium" data-id="element-429">
                        {resident.vitalSigns.temperature}°F
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded" data-id="element-430">
                      <div className="text-xs text-gray-500" data-id="element-431">Oxygen</div>
                      <div className="text-lg font-medium" data-id="element-432">
                        {resident.vitalSigns.oxygenLevel}
                      </div>
                    </div>
                  </div>}
                {resident.vitalSigns?.lastChecked && <div className="mt-2 text-xs text-gray-500" data-id="element-433">
                    Last checked: {formatDate(resident.vitalSigns.lastChecked)}
                  </div>}
              </div>
              <div data-id="element-434">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-435">
                  Primary Physician
                </h4>
                <p className="text-gray-900" data-id="element-436">{resident.primaryPhysician}</p>
              </div>
            </div>
          </div>
          {/* Medications */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-437">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-438">
              <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-439">
                <PillIcon className="h-5 w-5 mr-2 text-[#f6d052]" data-id="element-440" />
                Medications
              </h3>
            </div>
            <div className="overflow-hidden" data-id="element-441">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-442">
                <thead className="bg-gray-50" data-id="element-443">
                  <tr data-id="element-444">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-445">
                      Medication
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-446">
                      Dosage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-447">
                      Frequency
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-448">
                      Purpose
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-449">
                  {resident.medications?.map((med, index) => <tr key={index} data-id="element-450">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-451">
                        {med.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-452">
                        {med.dosage}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-453">
                        {med.frequency}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-454">
                        {med.purpose}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Right Column */}
        <div data-id="element-455">
          {/* Care Details */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-456">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-457">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-458">
                Care Details
              </h3>
            </div>
            <div className="p-6" data-id="element-459">
              <div className="mb-4" data-id="element-460">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-461">
                  Mobility
                </h4>
                <p className="text-gray-900" data-id="element-462">{resident.mobility}</p>
              </div>
              <div className="mb-4" data-id="element-463">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-464">
                  Dietary Restrictions
                </h4>
                <p className="text-gray-900" data-id="element-465">
                  {resident.dietaryRestrictions || 'None'}
                </p>
              </div>
              <div className="mb-4" data-id="element-466">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-467">
                  Activities
                </h4>
                <div className="flex flex-wrap gap-1 mt-1" data-id="element-468">
                  {resident.activities?.map((activity, index) => <span key={index} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded" data-id="element-469">
                      {activity}
                    </span>)}
                </div>
              </div>
              <div data-id="element-470">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-471">
                  Notes
                </h4>
                <p className="text-gray-900 text-sm" data-id="element-472">{resident.notes}</p>
              </div>
            </div>
          </div>
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow" data-id="element-473">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-474">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-475">
                Recent Activity
              </h3>
            </div>
            <div className="p-6" data-id="element-476">
              <div className="space-y-4" data-id="element-477">
                <div className="flex gap-3" data-id="element-478">
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-479">
                    <HeartPulseIcon className="h-5 w-5 text-blue-600" data-id="element-480" />
                  </div>
                  <div data-id="element-481">
                    <p className="text-sm font-medium" data-id="element-482">Vital signs checked</p>
                    <p className="text-xs text-gray-500" data-id="element-483">
                      Sep 15, 2023 - 9:30 AM
                    </p>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-484">
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-green-100 flex items-center justify-center" data-id="element-485">
                    <PillIcon className="h-5 w-5 text-green-600" data-id="element-486" />
                  </div>
                  <div data-id="element-487">
                    <p className="text-sm font-medium" data-id="element-488">
                      Medication administered
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-489">
                      Sep 15, 2023 - 8:00 AM
                    </p>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-490">
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-491">
                    <CalendarIcon className="h-5 w-5 text-purple-600" data-id="element-492" />
                  </div>
                  <div data-id="element-493">
                    <p className="text-sm font-medium" data-id="element-494">
                      Attended group activity
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-495">
                      Sep 14, 2023 - 2:00 PM
                    </p>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-496">
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-497">
                    <UserIcon className="h-5 w-5 text-amber-600" data-id="element-498" />
                  </div>
                  <div data-id="element-499">
                    <p className="text-sm font-medium" data-id="element-500">Family visit</p>
                    <p className="text-xs text-gray-500" data-id="element-501">
                      Sep 12, 2023 - 4:15 PM
                    </p>
                  </div>
                </div>
              </div>
              <button className="mt-4 w-full text-center text-sm text-[#1f58b0] hover:text-[#255db7] font-medium" data-id="element-502">
                View all activity
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>;
};